"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Inbox as InboxIcon,
  Circle,
  FolderKanban,
  Bookmark,
  FileText,
  LineChart,
  Activity as ActivityIcon,
  CheckSquare2,
} from "lucide-react";
import { useStore } from "@/lib/store";
import { createClient } from "@/lib/supabase/client";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { AgentHelpButton } from "./ui/AgentHelpButton";
import { ListView } from "./issues/ListView";
import { BoardView } from "./issues/BoardView";
import {
  CopyTaskViewButton,
  TaskFilterButton,
  TaskFilterSummaryBar,
} from "./issues/TaskFilters";
import { IssueDetail } from "./issues/IssueDetail";
import { CreateIssueModal } from "./issues/CreateIssueModal";
import { CommandMenu } from "./CommandMenu";
import { ProjectsView } from "./ProjectsView";
import { ProjectCommandCenter } from "./projects/ProjectCommandCenter";
import { TeamCommandCenter } from "./teams/TeamCommandCenter";
import {
  SaveCurrentViewButton,
  SavedViewsView,
} from "./views/SavedViews";
import { CyclesView } from "./CyclesView";
import { DocsView } from "./docs/DocsView";
import { NotesView } from "./NotesView";
import { ProgressView } from "./progress/ProgressView";
import { ActivityView } from "./ActivityView";
import { TriageView } from "./TriageView";
import { FeedbackOutboxView } from "./FeedbackOutboxView";
import { SharedView } from "./SharedView";
import { AgentRunsView } from "./AgentRunsView";
import { ChatTimelineView } from "./ChatTimelineView";
import { GraphView } from "./GraphView";
import { TrashPanel } from "./TrashPanel";
import { TagView } from "./TagView";
import { WorkspaceQuickstart } from "./WorkspaceQuickstart";
import { WorkspaceHome } from "./WorkspaceHome";
import { AttentionQueueView } from "./attention/AttentionQueueView";
import { PautaOnboarding } from "./onboarding/PautaOnboarding";
import { CreateClientModal } from "./CreateClientModal";
import { PautaMark } from "./brand/PautaMark";
import { ErrorBoundary } from "./ErrorBoundary";
import { exportWorkspaceZip } from "@/lib/export";
import {
  roleCanAdmin,
  roleCanWrite,
  usersForWorkspace,
  workspaceRoleForUser,
} from "@/lib/workspaceMembers";
import {
  applyTaskFilters,
  buildTaskViewUrl,
  clearTaskViewUrl,
  createEmptyTaskFilters,
  issueMatchesTaskSearch,
  type TaskFilters,
} from "@/lib/taskFilters";
import {
  CURRENT_ONBOARDING_VERSION,
  shouldShowOnboarding,
} from "@/lib/onboarding";
import { humanOwnerId } from "@/lib/taskOwnership";
import { useEntityRoute } from "./routing/useEntityRoute";
import { LiveWorkspaceView } from "./LiveWorkspaceView";
import { PersonalFocus } from "./focus/PersonalFocus";
import { usePersonalFocus } from "./focus/usePersonalFocus";
import { getPersonalFocusEvidence } from "@/lib/personalFocusEvidence";
import { PendingView } from "./pending/PendingView";
import { NotificationsView } from "./notifications/NotificationsView";
import { InboxUnreadMonitor } from "./notifications/InboxUnreadMonitor";
import { readWorkspaceNavigation, pendingDecisionsUrl, personalNavigationUrl, INBOX_SECTION_QUERY_KEY } from "@/lib/workspaceNavigation";
import type { NotificationSection } from "@/lib/personalNotifications";

type ActiveView =
  | "home"
  | "live"
  | "tasks"
  | "inbox"
  | "my-issues"
  | "awaiting-me"
  | "favorites"
  | "notifications"
  | "pending"
  | "projects"
  | "cycles"
  | "views"
  | "docs"
  | "notes"
  | "progress"
  | "activity"
  | "graph"
  | "agent-runs"
  | "chat"
  | "triage"
  | "feedback-outbox"
  | "shared"
  | "all"
  | "active"
  | "backlog"
  | "board"
  | `project:${string}`
  | `team:${string}`;

const TASK_VIEWS = new Set<ActiveView>([
  "tasks",
  "my-issues",
  "awaiting-me",
  "all",
  "active",
  "backlog",
  "board",
]);

const TEAM_SCOPED_TASK_VIEWS = new Set<ActiveView>(["all", "active", "backlog", "board"]);
const USER_SCOPED_TASK_VIEWS = new Set<ActiveView>(["my-issues", "awaiting-me"]);

function isActiveView(value: string): value is ActiveView {
  return (
    TASK_VIEWS.has(value as ActiveView) ||
    [
      "home",
      "inbox",
      "live",
      "favorites",
      "notifications",
      "pending",
      "projects",
      "cycles",
      "views",
      "docs",
      "notes",
      "progress",
      "activity",
      "graph",
      "agent-runs",
      "chat",
      "triage",
      "feedback-outbox",
      "shared",
    ].includes(value) ||
    value.startsWith("project:") ||
    value.startsWith("team:")
  );
}

export function Workspace({ initialSlug }: { initialSlug?: string } = {}) {
  const hydrated = useStore((s) => s.hydrated);
  const init = useStore((s) => s.init);
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const workspaces = useStore((s) => s.workspaces);
  const setActiveWorkspace = useStore((s) => s.setActiveWorkspace);

  useEffect(() => {
    void init();
  }, [init]);

  // Resolve the URL workspace before applying any entity selection. A missing
  // slug stays explicit; silently replacing it with another workspace loses intent.
  useEffect(() => {
    if (!hydrated || !initialSlug) return;
    const ws = workspaces.find((w) => w.slug === initialSlug);
    if (ws && ws.id !== activeWorkspaceId) setActiveWorkspace(ws.id);
  }, [hydrated, initialSlug, workspaces, activeWorkspaceId, setActiveWorkspace]);
  const workspace = useStore((s) =>
    s.workspaces.find((w) => w.id === s.activeWorkspaceId)
  );
  const issues = useStore((s) => s.issues);
  const teams = useStore((s) => s.teams);
  const projects = useStore((s) => s.projects);
  const labels = useStore((s) => s.labels);
  const cycles = useStore((s) => s.cycles);
  const docs = useStore((s) => s.docs);
  const progressCards = useStore((s) => s.progressCards);
  const users = useStore((s) => s.users);
  const currentUserId = useStore((s) => s.currentUserId);
  const focusScopeReady = hydrated && (!initialSlug || workspace?.slug === initialSlug);
  const focus = usePersonalFocus(focusScopeReady ? activeWorkspaceId : "", focusScopeReady ? currentUserId : "");
  const focusEvidence = useMemo(() => focus.model?.primary && workspace
    ? getPersonalFocusEvidence(focus.model.primary, workspace.slug, docs,
      [...focus.tasks, ...issues.filter((task) => task.workspaceId === activeWorkspaceId && !focus.tasks.some((candidate) => candidate.id === task.id))], focus.sourceRelations)
    : [], [focus.model, focus.tasks, focus.sourceRelations, workspace, docs, issues, activeWorkspaceId]);
  const onboardingVersion = useStore((s) => s.onboardingVersion);
  const workspaceMembers = useStore((s) => s.workspaceMembers);
  const activeTeamId = useStore((s) => s.activeTeamId);
  const setActiveTeam = useStore((s) => s.setActiveTeam);
  const selectedIssueId = useStore((s) => s.selectedIssueId);
  const selectIssue = useStore((s) => s.selectIssue);
  const setCreateOpen = useStore((s) => s.setCreateOpen);
  const setCommandOpen = useStore((s) => s.setCommandOpen);
  const updateIssue = useStore((s) => s.updateIssue);
  const addComment = useStore((s) => s.addComment);
  const refreshWorkspaceLiveData = useStore((s) => s.refreshWorkspaceLiveData);
  const refreshAgentLiveData = useStore((s) => s.refreshAgentLiveData);
  const refreshWorkspaceMembers = useStore((s) => s.refreshWorkspaceMembers);
  const completeOnboarding = useStore((s) => s.completeOnboarding);
  const currentRole = workspaceRoleForUser(
    workspaceMembers,
    activeWorkspaceId,
    currentUserId
  );
  const canWrite = roleCanWrite(currentRole);
  const canAdmin = roleCanAdmin(currentRole);

  // Realtime subscription — pushes changes from other sessions/agents into
  // this tab without requiring refresh. Issues, docs, notes, progress
  // cards, and comments are all in the supabase_realtime publication.
  // Coalesce events with a 250ms debounce
  // so a flurry (e.g. an agent inserting 5 issues in a loop) results in
  // one refetch.
  useEffect(() => {
    if (!activeWorkspaceId) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const ping = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void refreshWorkspaceLiveData(activeWorkspaceId);
      }, 250);
    };
    const sb = createClient();
    const ch = sb.channel(`ws:${activeWorkspaceId}:live`);
    for (const table of [
      "issues",
      "docs",
      "notes",
      "progress_cards",
      "comments",
    ]) {
      // Supabase JS realtime typing for postgres_changes is loose; cast.
      (ch as unknown as { on: (...a: unknown[]) => unknown }).on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table,
          // comments lacks workspace_id (issue_id only). Subscribe wide
          // and let the refetch handle filtering.
          ...(table !== "comments"
            ? { filter: `workspace_id=eq.${activeWorkspaceId}` }
            : {}),
        },
        ping
      );
    }
    ch.subscribe();
    return () => {
      if (timer) clearTimeout(timer);
      sb.removeChannel(ch);
    };
  }, [activeWorkspaceId, refreshWorkspaceLiveData]);

  // Agent lifecycle events can be much more frequent than content changes.
  // Keep them on a lightweight path that fetches only active/recent runs and
  // their sessions instead of reloading the entire workspace on every signal.
  useEffect(() => {
    if (!activeWorkspaceId) return;
    // Realtime only reports changes after subscription. Fetch immediately on
    // every workspace switch so a quiet tenant cannot inherit an empty slice
    // from another workspace's global result cap.
    void refreshAgentLiveData(activeWorkspaceId);
    let timer: ReturnType<typeof setTimeout> | null = null;
    const ping = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void refreshAgentLiveData(activeWorkspaceId);
      }, 100);
    };
    const sb = createClient();
    const ch = sb.channel(`ws:${activeWorkspaceId}:agent-live`);
    for (const table of ["agent_runs", "agent_sessions"]) {
      (ch as unknown as { on: (...a: unknown[]) => unknown }).on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table,
          filter: `workspace_id=eq.${activeWorkspaceId}`,
        },
        ping
      );
    }
    ch.subscribe();
    return () => {
      if (timer) clearTimeout(timer);
      sb.removeChannel(ch);
    };
  }, [activeWorkspaceId, refreshAgentLiveData]);

  // Team affiliation is presentation/relevance state for the sidebar, not an
  // authorization boundary. Listen separately because team_members has no
  // workspace_id column and does not belong in the heavier content refetch.
  useEffect(() => {
    if (!activeWorkspaceId) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const sb = createClient();
    const ch = sb
      .channel(`ws:${activeWorkspaceId}:team-members`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "team_members" },
        () => {
          if (timer) clearTimeout(timer);
          timer = setTimeout(() => {
            void refreshWorkspaceMembers(activeWorkspaceId);
          }, 250);
        }
      )
      .subscribe();

    return () => {
      if (timer) clearTimeout(timer);
      sb.removeChannel(ch);
    };
  }, [activeWorkspaceId, refreshWorkspaceMembers]);

  const [activeView, setActiveView] = useState<ActiveView>("home");
  const [inboxSection, setInboxSection] = useState<NotificationSection>("needs_me");
  const [inboxBadge, setInboxBadge] = useState<{workspaceId:string;userId:string;count:number|null} | null>(null);
  const inboxUnreadCount = inboxBadge?.workspaceId === activeWorkspaceId && inboxBadge?.userId === currentUserId ? inboxBadge.count : null;
  const entityRoute = useEntityRoute(initialSlug, (view) => {
    if (isActiveView(view)) setActiveView(view);
  });
  const readingDoc = activeView === "docs" && entityRoute.status === "ready" && entityRoute.target?.kind === "doc";
  const readingEntity = entityRoute.target?.kind === "doc" || entityRoute.target?.kind === "issue";
  const [viewMode, setViewMode] = useState<"list" | "board">("board");
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [onboardingReplayOpen, setOnboardingReplayOpen] = useState(false);
  const [onboardingDismissedThisSession, setOnboardingDismissedThisSession] =
    useState(false);
  const [onboardingDeferred, setOnboardingDeferred] = useState(() => {
    if (typeof window === "undefined") return false;
    const url = new URL(window.location.href);
    const entityLink = ["issue", "doc", "note", "project"].some((key) =>
      Boolean(url.searchParams.get(key)?.trim())
    );
    const initialHash = url.hash.slice(1);
    return entityLink || Boolean(initialHash && initialHash !== "home");
  });
  const currentUser = users.find((user) => user.id === currentUserId);
  const onboardingOpen =
    onboardingReplayOpen ||
    (!onboardingDismissedThisSession &&
      shouldShowOnboarding({
        hydrated,
        initialSlug,
        workspaceSlug: workspace?.slug,
        activeView,
        currentUserId,
        currentUser,
        onboardingVersion,
        hasEntityDeepLink: onboardingDeferred,
      }));
  // Search and structured filters live at workspace level so board + list use
  // one canonical pipeline. They are mirrored into query parameters, making a
  // filtered view survive login redirects and remain exactly copyable.
  const [issueSearch, setIssueSearch] = useState("");
  const [taskFilters, setTaskFilters] = useState<TaskFilters>(() => createEmptyTaskFilters());
  const [taskLocationHydrated, setTaskLocationHydrated] = useState(false);
  const [requestedTaskTeamId, setRequestedTaskTeamId] = useState<string | null>(null);
  const [taskScopeUserId, setTaskScopeUserId] = useState<string | null>(null);
  const [attentionSnoozes, setAttentionSnoozes] = useState<
    Record<string, number | null>
  >({});
  const [commandAskRequest, setCommandAskRequest] = useState<{
    nonce: number;
    scope: { projectId?: string; teamId?: string };
    label: string;
  } | null>(null);
  const taskFilterWorkspaceRef = useRef<string | null>(null);
  // Desktop sidebar collapse — persisted so the user's reading-mode preference
  // sticks across navigation. localStorage read happens in an effect to avoid
  // SSR hydration mismatch.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    setSidebarCollapsed(window.localStorage.getItem("pauta:sidebarCollapsed") === "1");
  }, []);
  function toggleSidebarCollapsed() {
    setSidebarCollapsed((v) => {
      const next = !v;
      if (typeof window !== "undefined") {
        window.localStorage.setItem("pauta:sidebarCollapsed", next ? "1" : "0");
      }
      return next;
    });
  }

  const prepareHomeForOnboarding = useCallback(() => {
    entityRoute.navigateView("home");
    setOnboardingDeferred(false);
  }, [entityRoute.navigateView]);

  useEffect(() => {
    const readLocation = (isNavigation: boolean) => {
      const location = readWorkspaceNavigation(window.location.href);
      // Hash is the browser's explicit target (including Cmd-click/sidebar
      // navigation). task_view is a fallback for auth redirects, which can
      // discard hashes. It must never override a newer hash target.
      const requestedView = location.requestedView;
      setInboxSection(location.inboxSection);
      if (isNavigation && requestedView === "home") setOnboardingDeferred(false);
      const params = new URL(window.location.href).searchParams;
      if (params.has("doc")) setActiveView("docs");
      else if (params.has("note")) setActiveView("notes");
      else if (requestedView && isActiveView(requestedView)) setActiveView(requestedView);
      const requestedTaskView = !!requestedView && TASK_VIEWS.has(requestedView as ActiveView);
      const reusableTaskState =
        requestedTaskView &&
        !location.explicitNavigation &&
        (!location.queryView || location.serializedTaskView);
      if (reusableTaskState) {
        setTaskFilters(location.filters);
        setIssueSearch(location.search);
        setTaskScopeUserId(
          USER_SCOPED_TASK_VIEWS.has(requestedView as ActiveView)
            ? location.scopeUserId
            : null
        );
      } else {
        setTaskFilters(createEmptyTaskFilters());
        setIssueSearch("");
        setTaskScopeUserId(null);
      }
      if (reusableTaskState && location.layout) {
        setViewMode(location.layout);
      } else if (requestedView === "board") {
        setViewMode("board");
      } else if (requestedView === "tasks") {
        setViewMode("list");
      }
      setRequestedTaskTeamId(
        reusableTaskState ? location.teamId : null
      );
      setTaskLocationHydrated(true);
    };
    const handleHashChange = () => readLocation(true);
    const handlePopState = () => readLocation(true);
    readLocation(false);
    window.addEventListener("hashchange", handleHashChange);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [prepareHomeForOnboarding]);

  // Filters are workspace-local state. Preserve an explicit copied link on
  // initial slug hydration, but reset when the user subsequently switches
  // workspace so an old assignee/project UUID cannot silently zero results.
  useEffect(() => {
    const routeWorkspaceReady = !initialSlug || workspace?.slug === initialSlug;
    if (!taskLocationHydrated || !activeWorkspaceId || !routeWorkspaceReady || entityRoute.hasIntent) return;
    const previousWorkspaceId = taskFilterWorkspaceRef.current;
    if (previousWorkspaceId && previousWorkspaceId !== activeWorkspaceId) {
      const location = readWorkspaceNavigation(window.location.href);
      const requestedView = location.requestedView;
      setInboxSection(location.inboxSection);
      const copiedTaskView =
        location.serializedTaskView &&
        !!requestedView &&
        isActiveView(requestedView) &&
        TASK_VIEWS.has(requestedView);

      if (copiedTaskView) {
        // Client-side route changes can preserve this component instance.
        // Re-read the destination URL instead of erasing its copied filters.
        setActiveView(requestedView);
        setTaskFilters(location.filters);
        setIssueSearch(location.search);
        setTaskScopeUserId(
          USER_SCOPED_TASK_VIEWS.has(requestedView)
            ? location.scopeUserId
            : null
        );
        setRequestedTaskTeamId(location.teamId);
        if (location.layout) setViewMode(location.layout);
      } else {
        setTaskFilters(createEmptyTaskFilters());
        setIssueSearch("");
        setTaskScopeUserId(null);
        setRequestedTaskTeamId(null);
        if (requestedView && isActiveView(requestedView)) setActiveView(requestedView);
      }
    }
    taskFilterWorkspaceRef.current = activeWorkspaceId;
  }, [activeWorkspaceId, initialSlug, taskLocationHydrated, workspace?.slug, entityRoute.hasIntent]);

  const deepLinkProjectId = useMemo(
    () =>
      typeof window !== "undefined"
        ? new URL(window.location.href).searchParams.get("project")
        : null,
    []
  );
  const deepLinkProjectApplied = useRef(!deepLinkProjectId);
  const deepLinkTeamId = useMemo(
    () =>
      typeof window !== "undefined"
        ? new URL(window.location.href).searchParams.get("team_home")
        : null,
    []
  );
  const deepLinkTeamApplied = useRef(!deepLinkTeamId);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!entityRoute.locationReady || !entityRoute.workspaceReady) return;
    if (entityRoute.target?.kind === "doc" && activeView !== "docs") return;
    if (entityRoute.target?.kind === "note" && activeView !== "notes") return;
    const target = `#${activeView}`;
    if (window.location.hash !== target) {
      const url = new URL(window.location.href);
      url.hash = activeView;
      history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
    }
  }, [activeView, entityRoute.locationReady, entityRoute.workspaceReady, entityRoute.target]);

  // Keep the browser tab title honest about which workspace + mode you're
  // in — critical when jumping between DEV and Client tabs.
  useEffect(() => {
    if (typeof document === "undefined" || !workspace) return;
    const prefix =
      workspace.category === "dev"
        ? "[DEV] "
        : workspace.category === "personal"
        ? "[Personal] "
        : "";
    document.title = `${prefix}${workspace.name} · Pauta`;
  }, [workspace]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (onboardingOpen) return;
      const target = e.target as HTMLElement;
      const typing =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCommandOpen(true);
        return;
      }
      // Escape must close the command menu even while its search input owns
      // focus. Handle it before the generic typing guard below.
      if (e.key === "Escape" && useStore.getState().commandOpen) {
        e.preventDefault();
        setCommandOpen(false);
        return;
      }
      if (typing) return;
      // Bail on any modifier key — `Cmd+C` is copy, `Cmd+G` is find-next,
      // etc. Single-letter shortcuts (Linear-style) are unmodified only.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if ((e.key === "c" || e.key === "C") && canWrite) {
        e.preventDefault();
        setCreateOpen(true);
      } else if (e.key === "/") {
        e.preventDefault();
        setCommandOpen(true);
      } else if (e.key === "Escape" && selectedIssueId) {
        selectIssue(null);
      } else if (e.key === "g") {
        const handler = (e2: KeyboardEvent) => {
          const nextView: ActiveView | null =
            e2.key === "h"
              ? "home"
              : e2.key === "i"
                ? "inbox"
                : e2.key === "m"
                  ? "my-issues"
                  : e2.key === "p"
                    ? "projects"
                    : e2.key === "c"
                      ? "cycles"
                      : e2.key === "a"
                        ? "all"
                        : e2.key === "d"
                          ? "docs"
                          : e2.key === "r"
                            ? "progress"
                            : null;
          if (nextView) {
            setTaskScopeUserId(null);
            if (nextView === "home") prepareHomeForOnboarding();
            entityRoute.navigateView(nextView);
          }
          window.removeEventListener("keydown", handler);
        };
        window.addEventListener("keydown", handler);
        setTimeout(() => window.removeEventListener("keydown", handler), 800);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    canWrite,
    onboardingOpen,
    prepareHomeForOnboarding,
    entityRoute.navigateView,
    selectedIssueId,
    selectIssue,
    setCommandOpen,
    setCreateOpen,
  ]);

  const wsIssues = useMemo(
    () => issues.filter((i) => i.workspaceId === activeWorkspaceId),
    [issues, activeWorkspaceId]
  );
  const wsTeams = useMemo(
    () => teams.filter((t) => t.workspaceId === activeWorkspaceId),
    [teams, activeWorkspaceId]
  );
  const savedViewTeams = useMemo(
    () =>
      canAdmin
        ? wsTeams
        : wsTeams.filter(
            (team) =>
              currentUserId !== null && team.memberIds.includes(currentUserId)
          ),
    [canAdmin, currentUserId, wsTeams]
  );
  const wsProjects = useMemo(
    () => projects.filter((p) => p.workspaceId === activeWorkspaceId),
    [projects, activeWorkspaceId]
  );

  // Project links use a query param because login redirects preserve it. Wait
  // for the target workspace data before mounting the command center, just as
  // issue deep links wait for their issue row.
  useEffect(() => {
    if (!entityRoute.locationReady) return;
    if (entityRoute.hasIntent) { deepLinkProjectApplied.current = true; return; }
    if (deepLinkProjectApplied.current || !deepLinkProjectId) return;
    if (wsProjects.some((candidate) => candidate.id === deepLinkProjectId)) {
      deepLinkProjectApplied.current = true;
      setActiveView(`project:${deepLinkProjectId}`);
    }
  }, [deepLinkProjectId, wsProjects, entityRoute.hasIntent, entityRoute.locationReady]);

  useEffect(() => {
    if (!entityRoute.locationReady) return;
    if (entityRoute.hasIntent) { deepLinkTeamApplied.current = true; return; }
    if (deepLinkTeamApplied.current || !deepLinkTeamId) return;
    if (wsTeams.some((candidate) => candidate.id === deepLinkTeamId)) {
      deepLinkTeamApplied.current = true;
      setActiveView(`team:${deepLinkTeamId}`);
    }
  }, [deepLinkTeamId, wsTeams, entityRoute.hasIntent, entityRoute.locationReady]);

  // Keep project/team homes in query params because auth redirects may discard
  // hashes. Wait for any initial deep link to resolve before rewriting it.
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !entityRoute.locationReady ||
      !deepLinkProjectApplied.current ||
      !deepLinkTeamApplied.current
    ) {
      return;
    }
    const url = new URL(window.location.href);
    const activeProjectId = activeView.startsWith("project:")
      ? activeView.slice("project:".length)
      : null;
    const activeTeamHomeId = activeView.startsWith("team:")
      ? activeView.slice("team:".length)
      : null;
    if (activeProjectId) url.searchParams.set("project", activeProjectId);
    else url.searchParams.delete("project");
    if (activeTeamHomeId) url.searchParams.set("team_home", activeTeamHomeId);
    else url.searchParams.delete("team_home");
    history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  }, [activeView]);
  const wsLabels = useMemo(() => {
    const teamIds = new Set(wsTeams.map((t) => t.id));
    return labels.filter((l) => teamIds.has(l.teamId));
  }, [labels, wsTeams]);
  const wsCycles = useMemo(() => {
    const teamIds = new Set(wsTeams.map((t) => t.id));
    return cycles.filter((c) => teamIds.has(c.teamId));
  }, [cycles, wsTeams]);
  const wsUsers = useMemo(
    () => usersForWorkspace(users, workspaceMembers, activeWorkspaceId),
    [activeWorkspaceId, users, workspaceMembers]
  );
  const effectiveTaskScopeUserId = USER_SCOPED_TASK_VIEWS.has(activeView)
    ? taskScopeUserId ?? currentUserId
    : null;
  const taskFilterData = useMemo(
    () => ({
      users: wsUsers,
      projects: wsProjects,
      teams: wsTeams,
      labels: wsLabels,
      cycles: wsCycles,
      estimates: [...new Set(wsIssues.flatMap((issue) => (issue.estimate == null ? [] : [issue.estimate])))]
        .sort((a, b) => a - b),
    }),
    [wsCycles, wsIssues, wsLabels, wsProjects, wsTeams, wsUsers]
  );
  const wsDocs = useMemo(
    () => docs.filter((d) => d.workspaceId === activeWorkspaceId),
    [docs, activeWorkspaceId]
  );
  const wsCards = useMemo(
    () => progressCards.filter((c) => c.workspaceId === activeWorkspaceId),
    [progressCards, activeWorkspaceId]
  );

  useEffect(() => {
    if (!activeWorkspaceId || !currentUserId) {
      setAttentionSnoozes({});
      return;
    }
    let canceled = false;
    const sb = createClient() as any;
    void sb
      .from("task_attention_states")
      .select("issue_id,kind,state,snoozed_until")
      .eq("workspace_id", activeWorkspaceId)
      .eq("user_id", currentUserId)
      .then(
        ({
          data,
        }: {
          data:
            | Array<{
                issue_id: string;
                kind: "assignment" | "review" | "mention" | "due";
                state: string;
                snoozed_until: string | null;
              }>
            | null;
        }) => {
          if (canceled) return;
          setAttentionSnoozes(
            Object.fromEntries(
              (data ?? []).map((row) => [
                `${row.kind}:${row.issue_id}`,
                row.state === "snoozed" && row.snoozed_until
                  ? Date.parse(row.snoozed_until)
                  : null,
              ])
            )
          );
        }
      );
    return () => {
      canceled = true;
    };
  }, [activeWorkspaceId, currentUserId]);

  // A copied team view carries its inherited team scope in the query string.
  // Apply it only after the destination workspace's teams have loaded.
  useEffect(() => {
    const routeWorkspaceReady = !initialSlug || workspace?.slug === initialSlug;
    if (!routeWorkspaceReady || !requestedTaskTeamId || wsTeams.length === 0) return;
    if (wsTeams.some((candidate) => candidate.id === requestedTaskTeamId)) {
      setActiveTeam(requestedTaskTeamId);
    }
    setRequestedTaskTeamId(null);
  }, [initialSlug, requestedTaskTeamId, setActiveTeam, workspace?.slug, wsTeams]);

  const filteredIssues = useMemo(() => {
    if (activeView.startsWith("project:")) {
      const pid = activeView.split(":")[1];
      return wsIssues.filter((i) => i.projectId === pid);
    }
    if (activeView === "inbox") return [];
    if (activeView === "my-issues") {
      return wsIssues.filter(
        (i) => humanOwnerId(i) === effectiveTaskScopeUserId
      );
    }
    if (activeView === "awaiting-me") {
      // PAUT-62: stuff I requested that the agent finished and bounced back
      // for my sign-off. Status `in_review` is the agent's signal that the
      // ball is in my court. Distinct from `My Issues` (assignee==me, things
      // I'm executing).
      return wsIssues.filter(
        (i) => i.requesterId === effectiveTaskScopeUserId && i.status === "in_review"
      );
    }
    if (activeView === "tasks") return wsIssues;
    if (activeView === "favorites") return [];
    if (activeView === "notifications") return [];

    if (!activeTeamId) return wsIssues;
    const teamIssues = wsIssues.filter((i) => i.teamId === activeTeamId);
    if (activeView === "active") {
      return teamIssues.filter(
        (i) => i.status === "in_progress" || i.status === "in_review" || i.status === "todo"
      );
    }
    if (activeView === "backlog") {
      return teamIssues.filter((i) => i.status === "backlog");
    }
    return teamIssues;
  }, [wsIssues, activeView, activeTeamId, effectiveTaskScopeUserId]);

  // Structured filters apply after the inherited view scope and before text
  // search. Board and list consume this same final array.
  const taskFilteredIssues = useMemo(
    () => applyTaskFilters(filteredIssues, taskFilters),
    [filteredIssues, taskFilters]
  );
  const labelNames = useMemo(
    () => new Map(wsLabels.map((label) => [label.id, label.name])),
    [wsLabels]
  );
  const searchFilteredIssues = useMemo(() => {
    if (!issueSearch.trim()) return taskFilteredIssues;
    return taskFilteredIssues.filter((issue) =>
      issueMatchesTaskSearch(issue, issueSearch, labelNames)
    );
  }, [issueSearch, labelNames, taskFilteredIssues]);

  // Keep activeTeamId in sync with the current workspace. We key the effect
  // on a stable team-id string (not the wsTeams array reference) so
  // `.filter()` returning a new array on every render can't loop this.
  // Also guard the setState call itself so writing the same value is a
  // no-op even if Zustand notifies.
  const firstTeamId = useMemo(() => wsTeams[0]?.id ?? null, [wsTeams]);
  const activeTeamBelongsToWorkspace = useMemo(
    () => !!activeTeamId && wsTeams.some((t) => t.id === activeTeamId),
    [activeTeamId, wsTeams]
  );
  useEffect(() => {
    if (requestedTaskTeamId) return;
    if (!activeTeamBelongsToWorkspace && activeTeamId !== firstTeamId) {
      setActiveTeam(firstTeamId);
    }
  }, [
    activeTeamBelongsToWorkspace,
    activeTeamId,
    firstTeamId,
    requestedTaskTeamId,
    setActiveTeam,
  ]);

  function handleSidebarNav(view: string) {
    setTaskScopeUserId(null);
    if (view === "home") prepareHomeForOnboarding();
    entityRoute.navigateView(view === "notifications" || view === "awaiting-me" ? "inbox" : view);
    if (view === "awaiting-me") setInboxSection("needs_me");
    if (view === "board") setViewMode("board");
    if (view === "tasks") setViewMode("list");
    setMobileSidebarOpen(false);
  }
  function handleSelectTeam(teamId: string, view: string) {
    setTaskScopeUserId(null);
    setActiveTeam(teamId);
    entityRoute.navigateView(view === "team" ? `team:${teamId}` : view);
    if (view === "board") setViewMode("board");
    setMobileSidebarOpen(false);
  }
  function handleOpenTrash() {
    if (!canWrite) return;
    setTrashOpen(true);
    setMobileSidebarOpen(false);
  }
  function handleCmdNavigate(view: string, teamId?: string | null) {
    setTaskScopeUserId(null);
    if (teamId) setActiveTeam(teamId);
    if (view === "home") prepareHomeForOnboarding();
    entityRoute.navigateView(view);
    if (view === "board") setViewMode("board");
    if (view === "tasks") setViewMode("list");
  }
  function handleOpenProject(projectId: string) {
    setTaskScopeUserId(null);
    entityRoute.navigateView(`project:${projectId}`);
  }

  function handleOpenOnboarding() {
    prepareHomeForOnboarding();
    setActiveView("home");
    setMobileSidebarOpen(false);
    setOnboardingReplayOpen(true);
  }

  async function handleExport() {
    if (!workspace) return;
    await exportWorkspaceZip({
      workspace,
      teams: wsTeams,
      labels: wsLabels,
      issues: wsIssues,
      projects: wsProjects,
      cycles: wsCycles,
      docs: wsDocs,
      progressCards: wsCards,
      users,
    });
  }

  const team = wsTeams.find((t) => t.id === activeTeamId);
  const project = activeView.startsWith("project:")
    ? wsProjects.find((p) => p.id === activeView.split(":")[1])
    : null;
  const viewTeam = activeView.startsWith("team:")
    ? wsTeams.find((candidate) => candidate.id === activeView.slice("team:".length))
    : team;

  const title =
    activeView === "home"
      ? workspace?.name || "Pauta"
      : activeView === "live"
      ? "Live workspace"
      : activeView === "tasks"
      ? "Tasks"
      : activeView === "inbox"
      ? "Inbox"
      : activeView === "my-issues"
      ? "Minhas tarefas"
      : activeView === "awaiting-me"
      ? "Decisões pendentes"
      : activeView === "favorites"
      ? "Favorites"
      : activeView === "notifications"
      ? "Inbox"
      : activeView === "pending"
      ? "Pendências"
      : activeView === "projects"
      ? "Projects"
      : activeView === "cycles"
      ? "Cycles"
      : activeView === "views"
      ? "Views"
      : activeView === "docs"
      ? "Docs"
      : activeView === "notes"
      ? "Notes"
      : activeView === "progress"
      ? "Progress"
      : activeView === "graph"
      ? "Graph"
      : activeView === "activity"
      ? "Activity"
      : activeView === "triage"
      ? "Triage"
      : activeView === "feedback-outbox"
      ? "Feedback outbox"
      : activeView === "shared"
      ? "Shared"
      : activeView === "agent-runs"
      ? "Agent runs"
      : project
      ? project.name
      : viewTeam
      ? viewTeam.name
      : workspace?.name || "Pauta";

  const titleIcon = project ? (
    <span>{project.icon}</span>
  ) : activeView === "tasks" ? (
    <CheckSquare2 size={13} />
  ) : activeView === "inbox" ? (
    <InboxIcon size={13} />
  ) : activeView === "my-issues" ? (
    <Circle size={13} />
  ) : activeView === "projects" ? (
    <FolderKanban size={13} />
  ) : activeView === "cycles" ? (
    <Bookmark size={13} />
  ) : activeView === "docs" ? (
    <FileText size={13} />
  ) : activeView === "progress" ? (
    <LineChart size={13} />
  ) : activeView === "activity" ? (
    <ActivityIcon size={13} />
  ) : viewTeam ? (
    <span>{viewTeam.icon}</span>
  ) : workspace ? (
    <span>{workspace.icon}</span>
  ) : null;

  const titleColor = project?.color || viewTeam?.color || workspace?.color;

  const isTaskList =
    activeView === "tasks" ||
    activeView === "my-issues" ||
    activeView === "awaiting-me" ||
    activeView === "all" ||
    activeView === "active" ||
    activeView === "backlog" ||
    activeView === "board";

  const taskScopeUser = effectiveTaskScopeUserId
    ? wsUsers.find((candidate) => candidate.id === effectiveTaskScopeUserId)
    : null;
  const taskScopePersonLabel =
    effectiveTaskScopeUserId && effectiveTaskScopeUserId !== currentUserId
      ? taskScopeUser?.name || taskScopeUser?.email || "shared user"
      : "me";

  const inheritedTaskScopes = useMemo(() => {
    const scopes: string[] = [];
    if (activeView === "my-issues") scopes.push(`Assigned to ${taskScopePersonLabel}`);
    if (activeView === "awaiting-me") scopes.push(`Reported by ${taskScopePersonLabel}`, "In review");
    if (TEAM_SCOPED_TASK_VIEWS.has(activeView) && team) scopes.push(`Team: ${team.name}`);
    if (activeView === "active") scopes.push("Active statuses");
    if (activeView === "backlog") scopes.push("Backlog");
    return scopes;
  }, [activeView, taskScopePersonLabel, team]);

  // Keep the current task view fully reconstructable without a server round
  // trip. task_view/task_team survive auth redirects; the hash remains for
  // normal in-app navigation.
  useEffect(() => {
    const routeWorkspaceReady = !initialSlug || workspace?.slug === initialSlug;
    if (
      !taskLocationHydrated ||
      !entityRoute.locationReady ||
      (entityRoute.target?.kind === "doc" && activeView !== "docs") ||
      (entityRoute.target?.kind === "note" && activeView !== "notes") ||
      !routeWorkspaceReady ||
      requestedTaskTeamId ||
      typeof window === "undefined"
    ) {
      return;
    }
    const next = new URL(
      isTaskList
        ? buildTaskViewUrl(window.location.href, taskFilters, {
            view: activeView,
            teamId: TEAM_SCOPED_TASK_VIEWS.has(activeView) ? activeTeamId : null,
            search: issueSearch,
            layout: viewMode,
            scopeUserId: USER_SCOPED_TASK_VIEWS.has(activeView)
              ? effectiveTaskScopeUserId
              : null,
          })
        : activeView === "inbox" || activeView === "activity"
          ? personalNavigationUrl(window.location.href, activeView, inboxSection)
          : clearTaskViewUrl(window.location.href)
    );
    next.searchParams.delete("taskView");
    if (activeView === "inbox") next.searchParams.set(INBOX_SECTION_QUERY_KEY, inboxSection);
    else next.searchParams.delete(INBOX_SECTION_QUERY_KEY);
    const nextRelative = `${next.pathname}${next.search}${next.hash}`;
    const currentRelative = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (nextRelative !== currentRelative) history.replaceState(null, "", nextRelative);
  }, [
    activeTeamId,
    activeView,
    inboxSection,
    entityRoute.locationReady,
    entityRoute.target,
    effectiveTaskScopeUserId,
    isTaskList,
    issueSearch,
    taskFilters,
    taskLocationHydrated,
    initialSlug,
    requestedTaskTeamId,
    viewMode,
    workspace?.slug,
  ]);

  if (!hydrated || !entityRoute.locationReady || (initialSlug && workspace?.slug !== initialSlug && entityRoute.status === "loading")) {
    return (
      <div className="h-screen w-screen flex items-center justify-center text-[var(--fg-muted)]">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-[var(--border-strong)] border-t-[var(--accent)] rounded-full animate-spin" />
          <span className="text-[12.5px]">Loading…</span>
        </div>
      </div>
    );
  }

  if (!currentUserId && entityRoute.status !== "error") {
    const next = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    return <div className="h-dvh flex flex-col items-center justify-center gap-4 p-6 text-center">
      <h1 className="text-lg font-semibold">Sign in to open this link</h1>
      <a href={`/login?next=${encodeURIComponent(next)}`} className="min-h-11 flex items-center text-[var(--accent)]">Sign in</a>
    </div>;
  }

  if (initialSlug && !workspaces.some((w) => w.slug === initialSlug)) {
    return <div className="h-dvh flex flex-col items-center justify-center gap-4 p-6 text-center">
      <h1 className="text-lg font-semibold">{entityRoute.status === "error" ? "Couldn’t load this workspace" : "Workspace unavailable"}</h1>
      <p>{entityRoute.status === "error" ? "Check your connection and try again." : "It could not be found, or you do not have access."}</p>
      {entityRoute.status === "error" && <button onClick={entityRoute.retry} className="min-h-11 px-4">Try again</button>}
      <a href="/" className="min-h-11 flex items-center text-[var(--accent)]">Choose a workspace</a>
    </div>;
  }

  return (
    <div className="h-dvh w-full flex flex-col overflow-hidden bg-[var(--bg)] text-[var(--fg)]">
      {workspace?.category === "dev" && (
        <div
          className="shrink-0 h-7 flex items-center justify-center gap-2 text-[11px] font-bold uppercase tracking-wider text-white"
          style={{
            background:
              "repeating-linear-gradient(45deg, var(--orange) 0 12px, color-mix(in srgb, var(--orange) 80%, #000) 12px 24px)",
          }}
        >
          <span>DEV MODE</span>
          <span className="opacity-70">·</span>
          <span>{workspace.name}</span>
          <span className="opacity-70">·</span>
          <span className="opacity-70 normal-case tracking-normal font-medium">
            Agents in this workspace are building the app, not serving a client
          </span>
        </div>
      )}
      <div className="flex-1 flex overflow-hidden">
      {activeWorkspaceId && currentUserId && <InboxUnreadMonitor key={`${activeWorkspaceId}:${currentUserId}`} workspaceId={activeWorkspaceId} userId={currentUserId} onCount={(count) => setInboxBadge({ workspaceId: activeWorkspaceId, userId: currentUserId, count })} />}
      <Sidebar
        inboxUnreadCount={inboxUnreadCount}
        activeView={activeView}
        onNavigate={handleSidebarNav}
        activeTeamId={activeTeamId}
        onSelectTeam={handleSelectTeam}
        onExport={handleExport}
        onOpenTrash={handleOpenTrash}
        mobileOpen={mobileSidebarOpen}
        onCloseMobile={() => setMobileSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        onOpenOnboarding={handleOpenOnboarding}
      />
      <main className="flex-1 flex flex-col min-w-0">
        {!readingDoc && <TopBar
          title={title}
          icon={titleIcon}
          color={titleColor}
          view={viewMode}
          onViewChange={setViewMode}
          count={isTaskList ? searchFilteredIssues.length : undefined}
          showViewToggle={isTaskList}
          onHamburger={() => setMobileSidebarOpen(true)}
          mobileMenuOpen={mobileSidebarOpen}
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={toggleSidebarCollapsed}
          searchValue={isTaskList ? issueSearch : undefined}
          onSearchChange={isTaskList ? setIssueSearch : undefined}
          searchPlaceholder="Search tasks — ENG-33, title, label…"
          rightExtra={
            isTaskList ? (
              <div className="flex items-center gap-1">
                <TaskFilterButton
                  filters={taskFilters}
                  onChange={setTaskFilters}
                  data={taskFilterData}
                />
                <CopyTaskViewButton
                  getUrl={() =>
                    buildTaskViewUrl(window.location.href, taskFilters, {
                      view: activeView,
                      teamId: TEAM_SCOPED_TASK_VIEWS.has(activeView)
                        ? activeTeamId
                        : null,
                      search: issueSearch,
                      layout: viewMode,
                      scopeUserId: USER_SCOPED_TASK_VIEWS.has(activeView)
                        ? effectiveTaskScopeUserId
                        : null,
                      stripEntityDeepLinks: true,
                    })
                  }
                />
                {workspace && (
                  <SaveCurrentViewButton
                    workspaceId={workspace.id}
                    currentUserId={currentUserId}
                    teams={savedViewTeams}
                    canShare={canWrite}
                    descriptor={{
                      baseView: activeView,
                      teamId: TEAM_SCOPED_TASK_VIEWS.has(activeView)
                        ? activeTeamId
                        : null,
                      filters: taskFilters,
                      search: issueSearch,
                      layout: viewMode,
                      scopeUserId: USER_SCOPED_TASK_VIEWS.has(activeView)
                        ? effectiveTaskScopeUserId
                        : null,
                    }}
                  />
                )}
                <AgentHelpButton kind="issues" />
              </div>
            ) : activeView === "triage" || activeView === "feedback-outbox" ? (
              <AgentHelpButton kind="feedback" />
            ) : undefined
          }
        />}
        {!readingEntity && activeView !== "inbox" && activeView !== "notifications" && activeView !== "activity" && <PersonalFocus
          variant="compact"
          model={focus.model} loading={focus.loading} saving={focus.saving} error={focus.error}
          period={focus.period} onPeriodChange={focus.setPeriod}
          filters={focus.filters} onFiltersChange={focus.setFilters} filterOptions={focus.filterOptions}
          workspaceSlug={workspace?.slug ?? ""} onOpenTask={selectIssue}
          onConfirm={focus.confirm} onClear={focus.clear} onRetry={focus.retry}
          onNavigateTasks={() => handleSidebarNav("my-issues")}
          onShowContext={() => handleSidebarNav("home")}
        />}
        {isTaskList && (
          <TaskFilterSummaryBar
            filters={taskFilters}
            onChange={setTaskFilters}
            searchValue={issueSearch}
            onSearchChange={setIssueSearch}
            data={taskFilterData}
            inheritedScopes={inheritedTaskScopes}
            resultCount={searchFilteredIssues.length}
            totalCount={filteredIssues.length}
          />
        )}
        <ErrorBoundary label={`workspace:${activeWorkspaceId}`} resetKey={activeWorkspaceId}>
        <motion.div
          key={activeWorkspaceId + activeView + viewMode}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15 }}
          className="flex-1 flex flex-col min-h-0"
        >
          {entityRoute.hasIntent && entityRoute.status !== "ready" ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6 text-center" role="status">
              <h2 className="text-[15px] font-semibold">{entityRoute.status === "loading" ? "Opening link…" : entityRoute.status === "error" ? "Couldn’t load this link" : "Not found or no access"}</h2>
              <p className="max-w-sm text-[13px] text-[var(--fg-secondary)]">{entityRoute.status === "loading" ? "Loading the linked item in this workspace." : entityRoute.status === "error" ? "Check your connection and try again." : "This item may have been removed, or you may not have access in this workspace."}</p>
              {entityRoute.status === "error" && <button onClick={entityRoute.retry} className="min-h-11 rounded-md border border-[var(--border)] px-4">Try again</button>}
              {entityRoute.status !== "loading" && <button onClick={() => handleSidebarNav("home")} className="min-h-11 px-4 text-[var(--accent)]">Go to Home</button>}
            </div>
          ) : activeView === "home" ? (
            <WorkspaceHome
              onNavigate={handleSidebarNav}
              onOpenCommand={() => setCommandOpen(true)}
              focusSlot={<PersonalFocus
                model={focus.model} loading={focus.loading} saving={focus.saving}
                period={focus.period} onPeriodChange={focus.setPeriod}
                filters={focus.filters} onFiltersChange={focus.setFilters} filterOptions={focus.filterOptions}
                error={focus.error ?? (focus.sourceError ? "Parte das referências não pôde ser carregada. Abra o ticket para consultar o contexto registrado." : null)}
                workspaceSlug={workspace?.slug ?? ""} onOpenTask={selectIssue}
                onConfirm={focus.confirm} onClear={focus.clear} onRetry={focus.retry}
                onNavigateTasks={() => handleSidebarNav("my-issues")}
                onShowContext={() => handleSidebarNav("home")} evidence={focusEvidence}
              />}
            />
          ) : activeView === "live" ? (
            <LiveWorkspaceView />
          ) : activeView === "projects" ? (
            <ProjectsView onOpenProject={handleOpenProject} />
          ) : activeView.startsWith("project:") ? (
            <ProjectCommandCenter
              projectId={activeView.slice("project:".length)}
              onBack={() => entityRoute.navigateView("projects")}
              onNavigate={handleSidebarNav}
              onOpenAsk={() => {
                const projectId = activeView.slice("project:".length);
                const name =
                  wsProjects.find((candidate) => candidate.id === projectId)
                    ?.name ?? "this project";
                setCommandAskRequest({
                  nonce: Date.now(),
                  scope: { projectId },
                  label: name,
                });
                setCommandOpen(true);
              }}
              canWrite={canWrite}
            />
          ) : activeView.startsWith("team:") ? (
            <TeamCommandCenter
              teamId={activeView.slice("team:".length)}
              canWrite={canWrite}
              onOpenTask={selectIssue}
              onOpenProject={handleOpenProject}
              onOpenDoc={entityRoute.openDoc}
              onOpenAsk={() => {
                const teamId = activeView.slice("team:".length);
                const name =
                  wsTeams.find((candidate) => candidate.id === teamId)?.name ??
                  "this team";
                setCommandAskRequest({
                  nonce: Date.now(),
                  scope: { teamId },
                  label: name,
                });
                setCommandOpen(true);
              }}
              onNavigateTasks={() => {
                const teamId = activeView.slice("team:".length);
                setActiveTeam(teamId);
                setActiveView("all");
              }}
            />
          ) : activeView === "cycles" ? (
            <CyclesView />
          ) : activeView === "views" ? (
            activeWorkspaceId ? (
              <SavedViewsView
                workspaceId={activeWorkspaceId}
                currentUserId={currentUserId}
                canWrite={canWrite}
                canAdmin={canAdmin}
              />
            ) : null
          ) : activeView === "docs" ? (
            <DocsView
              canWrite={canWrite}
              onOpenDoc={entityRoute.openDoc}
              onNavigateHome={() => handleSidebarNav("home")}
              onOpenNavigation={() => setMobileSidebarOpen(true)}
            />
          ) : activeView === "notes" ? (
            <NotesView canWrite={canWrite} />
          ) : activeView === "progress" ? (
            <ProgressView canWrite={canWrite} />
          ) : activeView === "graph" ? (
            <GraphView />
          ) : activeView === "activity" ? (
            <ActivityView />
          ) : activeView === "agent-runs" ? (
            <AgentRunsView />
          ) : activeView === "chat" ? (
            <ChatTimelineView />
          ) : activeView === "triage" ? (
            <TriageView canWrite={canWrite} />
          ) : activeView === "feedback-outbox" ? (
            <FeedbackOutboxView canWrite={canWrite} />
          ) : activeView === "shared" ? (
            <SharedView canWrite={canWrite} />
          ) : activeView === "favorites" ? (
            <EmptyState title="No favorites yet" body="Star tasks and projects to pin them here." />
          ) : activeView === "inbox" || activeView === "notifications" ? (
            <NotificationsView key={`${activeWorkspaceId}:${currentUserId}`} workspaceId={activeWorkspaceId}
              currentUserId={currentUserId} section={inboxSection} onSectionChange={setInboxSection}
              decisionsHref={pendingDecisionsUrl(typeof window !== "undefined" ? window.location.href : `https://local.invalid/${workspace?.slug ?? ""}`)}
              onOpenDecisions={() => { setTaskScopeUserId(null); setTaskFilters(createEmptyTaskFilters()); setIssueSearch(""); entityRoute.navigateView("awaiting-me"); }}
              onOpenEntity={(kind, id) => {
                if (kind === "issue") selectIssue(id);
                else if (kind === "doc") entityRoute.openDoc(id);
                else entityRoute.openNote(id);
              }} />
          ) : activeView === "pending" ? (
            <PendingView key={`${activeWorkspaceId}:${currentUserId}`} workspaceId={activeWorkspaceId}
              userId={currentUserId} canWrite={canWrite} onOpenIssue={selectIssue}
              onResolved={() => { void refreshWorkspaceLiveData(activeWorkspaceId); focus.retry(); }} />
          ) : activeView === "awaiting-me" ? (
            <AttentionQueueView
              issues={searchFilteredIssues}
              canWrite={canWrite}
              mode={activeView === "awaiting-me" ? "awaiting" : "inbox"}
              currentUserId={effectiveTaskScopeUserId}
              onOpen={selectIssue}
              onUpdate={async (issueId, patch) => {
                updateIssue(issueId, patch);
              }}
              onComment={async (issueId, body) => {
                await addComment(issueId, body);
              }}
              snoozedUntilByIssueId={Object.fromEntries(
                wsIssues.map((issue) => [
                  issue.id,
                  attentionSnoozes[
                    `${
                      activeView === "awaiting-me" ? "review" : "assignment"
                    }:${issue.id}`
                  ] ?? null,
                ])
              )}
              onSnooze={async (issueId, until) => {
                if (!activeWorkspaceId || !currentUserId) return;
                const kind =
                  activeView === "awaiting-me" ? "review" : "assignment";
                const attentionKey = `${kind}:${issueId}`;
                const previous = attentionSnoozes[attentionKey] ?? null;
                setAttentionSnoozes((state) => ({
                  ...state,
                  [attentionKey]: until,
                }));
                const { error } = await (createClient() as any)
                  .from("task_attention_states")
                  .upsert(
                    {
                      workspace_id: activeWorkspaceId,
                      user_id: currentUserId,
                      issue_id: issueId,
                      kind,
                      state: until == null ? "unread" : "snoozed",
                      snoozed_until:
                        until == null ? null : new Date(until).toISOString(),
                      last_event_at: new Date().toISOString(),
                    },
                    {
                      onConflict: "workspace_id,user_id,issue_id,kind",
                    }
                  );
                if (error) {
                  setAttentionSnoozes((state) => ({
                    ...state,
                    [attentionKey]: previous,
                  }));
                  throw error;
                }
              }}
            />
          ) : workspace &&
            wsIssues.length === 0 &&
            !activeTeamId /* only at workspace root, not inside a team */ &&
            (workspace.category === "dev"
              ? wsTeams.length <= 1 // dev has an auto-created default team
              : wsTeams.length === 0) &&
            activeView === "all" ? (
            <WorkspaceQuickstart
              workspace={workspace}
              onAddClient={() => canWrite && setClientModalOpen(true)}
              onOpenAgents={() => canAdmin && useStore.getState().requestAgentsModal()}
            />
          ) : viewMode === "board" ? (
            <BoardView issues={searchFilteredIssues} canWrite={canWrite} />
          ) : (
            <ListView issues={searchFilteredIssues} canWrite={canWrite} />
          )}
        </motion.div>
        </ErrorBoundary>
      </main>
      {entityRoute.status === "ready" && <IssueDetail canWrite={canWrite} />}
      {canWrite && (
        <CreateIssueModal
          defaultProjectId={project?.id ?? null}
          defaultTeamId={
            project?.teamIds.includes(activeTeamId ?? "")
              ? activeTeamId
              : project?.teamIds[0] ?? null
          }
        />
      )}
      <CommandMenu
        onNavigate={handleCmdNavigate}
        askRequest={commandAskRequest}
      />
      {canWrite && (
        <TrashPanel
          open={trashOpen}
          onOpenChange={setTrashOpen}
          canAdmin={canAdmin}
        />
      )}
      <TagView />
      {canWrite && (
        <CreateClientModal
          open={clientModalOpen}
          onClose={() => setClientModalOpen(false)}
        />
      )}
      {workspace && currentUserId && onboardingOpen && (
        <PautaOnboarding
          open
          workspaceId={workspace.id}
          workspaceName={workspace.name}
          currentUserId={currentUserId}
          onPersistCompletion={() => completeOnboarding(CURRENT_ONBOARDING_VERSION)}
          onDismiss={() => {
            setOnboardingReplayOpen(false);
            setOnboardingDismissedThisSession(true);
          }}
          onExplore={handleSidebarNav}
        />
      )}
      </div>
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center max-w-sm px-6">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[var(--bg-tertiary)] border border-[var(--border)] flex items-center justify-center">
          <PautaMark size={32} />
        </div>
        <h3 className="text-[15px] font-semibold mb-1">{title}</h3>
        <p className="text-[13px] text-[var(--fg-tertiary)]">{body}</p>
      </div>
    </div>
  );
}
